import { useEffect, useState } from "react";
import { Row, Col, Tab, Tabs, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CompanyCard from "@/pages/shared/sections/CompanyCard";
import api from "@/api/api.js";
import { capitalizeFirstLetter } from "@/utils/stringUtils.js";
import FiscalDocumentList from "@/pages/shared/sections/FiscalDocumentList.jsx";
import CompanyRequestTickets from "@/pages/shared/sections/CompanyRequestTickets.jsx";
import FiscalDocumentUpload from "@/pages/shared/sections/FiscalDocumentUpload.jsx";
import SignatureUpload from "@/pages/shared/sections/SignatureUpload.jsx";
import DigitalSignatureList from "@/pages/shared/sections/DigitalSignatureList.jsx";
import CompanyOwnerCard from "@/pages/shared/sections/CompanyOwnerCard.jsx";
import RegisterQuotaButtons from "@/pages/shared/sections/RegisterQuotaButtons.jsx";
import { ROLES } from "@/config/roles.js";

const DetailCompany = () => {
  const { t } = useTranslation();
  const userRoles = JSON.parse(sessionStorage.getItem("roles") || "[]");
  const isMaster = userRoles.includes(ROLES.SUPER_ADMIN);
  // Entregar documentos fiscales es trabajo del contador, no sólo del master.
  const deliversDocuments = isMaster || userRoles.includes(ROLES.ADMIN);
  const [activeTab, setActiveTab] = useState("folios");
  const [refreshCertList, setRefreshCertList] = useState(0);
  const [refreshSignatureList, setRefreshSignatureList] = useState(0);
  const [refreshOpinionList, setRefreshOpinionList] = useState(0);
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleUploadSuccess = () => {
    setActiveTab("constancias");
    setRefreshCertList((prev) => prev + 1);
  };

  const handleOpinionUploadSuccess = () => {
    setActiveTab("opiniones");
    setRefreshOpinionList((prev) => prev + 1);
  };

  const handleSignatureUploadSuccess = () => {
    setActiveTab("firmas");
    setRefreshSignatureList((prev) => prev + 1);
  };

  const fetchCompanyData = async () => {
    try {
      const response = await api.get(`/companies/${id}`);
      setCompany(response.data);
    } catch {
      setError("Company not found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.debug("Calling fetchCompanyData");
    fetchCompanyData();
  }, [id]);

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <Row>
        <Col xs={4}>
          <CompanyCard company={company} />
          <CompanyOwnerCard userId={company?.user?.["@id"]?.split("/").pop()} />
          {deliversDocuments && (
            <FiscalDocumentUpload
              companyId={company.id}
              type="fiscalCertificate"
              onUploadSuccess={handleUploadSuccess}
            />
          )}
          {deliversDocuments && (
            <FiscalDocumentUpload
              companyId={company.id}
              type="complianceOpinion"
              onUploadSuccess={handleOpinionUploadSuccess}
            />
          )}
          {deliversDocuments && (
            <RegisterQuotaButtons
              userId={company?.user?.["@id"]?.split("/").pop()}
            />
          )}
          {isMaster && (
            <SignatureUpload
              asOwner={false}
              companyId={company.id}
              onUploadSuccess={handleSignatureUploadSuccess}
            />
          )}
        </Col>
        <Col xs={8}>
          <Card>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              id="Tabs-DetailCompany"
              className="mb-3 text-capitalize-first"
              justify
            >
              <Tab
                eventKey="folios"
                title={capitalizeFirstLetter(t("Solicitud de Folios"))}
              >
                <CompanyRequestTickets
                  key={refreshCertList}
                  company={company}
                />
              </Tab>
              <Tab
                eventKey="constancias"
                title={capitalizeFirstLetter(t("Constancias"))}
              >
                <FiscalDocumentList key={refreshCertList} company={company} />
              </Tab>
              <Tab
                eventKey="opiniones"
                title={capitalizeFirstLetter(t("ui.complianceOpinion"))}
              >
                <FiscalDocumentList
                  key={refreshOpinionList}
                  company={company}
                  type="complianceOpinion"
                />
              </Tab>
              <Tab
                eventKey="firmas"
                title={capitalizeFirstLetter(t("Firmas digitales"))}
              >
                <DigitalSignatureList
                  key={refreshSignatureList}
                  company={company}
                />
              </Tab>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DetailCompany;
